import { useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useRouter } from 'next/router';
import segmentStorage from '@events/segmentStorage';
import {
  GOAL_UID,
  GOAL_NAME,
  IS_SUBSCRIPTION_ACTIVE,
  SUBSCRIPTION_DURATION,
  SUBSCRIPTION_END_DATE,
  LANGUAGE
} from '@events/segmentProps';
import { getDaysLapsed } from '@utils/dates';
import { getSubscription } from '@models/Me';
import { setLps } from '@actions/currentPageInfo';
import { getCurrentPageName, getTraversalPath } from '@selectors/index';

export default function SegmentAnalytics() {
  const {
    query: { goalUID: routeGoalUID },
    route,
    events
  } = useRouter();
  const meUid = useSelector(({ auth }) => auth.me.uid);
  const meUsername = useSelector(({ auth }) => auth.me.username);
  const language = useSelector(({ auth }) => auth.me.language);
  const state = useSelector(({ auth }) => auth.me.state);
  const dateJoined = useSelector(({ auth }) => auth.me.dateJoined);
  const isEmailVerified = useSelector(({ auth }) => auth.me.isEmailVerified);
  const isPhoneVerified = useSelector(({ auth }) => auth.me.isPhoneVerified);
  const isInvited = useSelector(({ auth }) => auth.me.isInvited || false);
  const selectedGoal = useSelector(({ auth }) => auth.selectedGoal ?? {});
  const platform = useSelector(({ helper }) => helper.isMobile);
  const { uid = 'NA', name = 'NA' } = selectedGoal;
  const plusSubscriptions = useSelector(
    ({ auth }) => auth.me.plusSubscriptions
  );
  const goalUID = routeGoalUID || uid;
  const goalName =
    useSelector(({ topologies }) => topologies.data?.[goalUID]?.name || '') ||
    name;
  const { subscription = {}, expired_at, time_remaining } =
    getSubscription(plusSubscriptions, goalUID) ?? {};
  const isSubscriptionActive = time_remaining > 0;
  const currentScreen = useSelector(getCurrentPageName);
  const currentPath = useSelector(getTraversalPath);
  const lps = useSelector(({ currentPageInfo }) => currentPageInfo.lps || '');
  const dispatch = useDispatch();
  const prevLpsRef = useRef('');
  useEffect(() => {
    const routeChangeStart = () => {
      prevLpsRef.current = lps;
      dispatch(setLps(currentScreen));
    };
    const routeChangeComplete = () => {
      prevLpsRef.current = '';
    };
    const routeChangeError = () => {
      dispatch(setLps(prevLpsRef.current));
      prevLpsRef.current = '';
    };
    events.on('routeChangeStart', routeChangeStart);
    events.on('routeChangeComplete', routeChangeComplete);
    events.on('routeChangeError', routeChangeError);
    return () => {
      events.off('routeChangeStart', routeChangeStart);
      events.off('routeChangeComplete', routeChangeComplete);
      events.off('routeChangeError', routeChangeError);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentScreen, lps]);
  useEffect(() => {
    let screen = currentScreen;
    const path = currentPath;
    if (!screen) {
      screen = route;
      // path = `${currentPath} > ${route}`;
    }
    const segmentData = {
      learner_uid: meUid,
      learner_username: meUsername,
      platform: platform === 7 ? 'MWeb' : 'Web',
      [GOAL_UID]: uid,
      [GOAL_NAME]: name,
      [LANGUAGE]: language,
      state,
      days_since_joining: getDaysLapsed(dateJoined),
      [IS_SUBSCRIPTION_ACTIVE]: time_remaining > 0,
      is_verified: (isEmailVerified && isPhoneVerified) || false,
      is_invited: isInvited,
      current_screen: screen,
      path,
      last_primary_source: lps
    };
    if (isSubscriptionActive) {
      segmentData[SUBSCRIPTION_DURATION] = subscription.duration;
      segmentData[SUBSCRIPTION_END_DATE] = expired_at;
    }
    segmentStorage.setItem('segmentData', segmentData);
  }, [
    dateJoined,
    expired_at,
    isEmailVerified,
    isPhoneVerified,
    isSubscriptionActive,
    language,
    meUid,
    meUsername,
    name,
    platform,
    selectedGoal,
    state,
    subscription,
    time_remaining,
    uid,
    isInvited,
    currentScreen,
    currentPath,
    goalUID,
    goalName,
    route,
    lps
  ]);
  return null;
}
